import {
  Button,
  Dialog,
  DialogBody,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
} from "@fluentui/react-components";
import { Dismiss24Regular } from "@fluentui/react-icons";
import { AppSpinner } from "components/AppSpinner/AppSpinner";
import { devices } from "library/constants";
import { extractFileNameFromUrl } from "library/utils";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetAllImages } from "state/queries/useGetAllImages";
import styled from "styled-components";
import { ImageItem } from "./ImageItem/ImageItem";
import { ManageImagesToolbar } from "./ManageImagesToolbar";
import { AddImages } from "./AddImages/AddImages";

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
  onSelectImage?: (event: any, image?: string) => void;
};

export const ManageImagesDialog = ({ open, setOpen, onSelectImage }: Props) => {
  const { t } = useTranslation();

  const { data: allImages, isLoading: loadingImages } = useGetAllImages({
    enabled: true,
  });
  const [searchValue, setSearchValue] = useState("");

  const handleSearch = useCallback((value) => {
    setSearchValue(value.toLowerCase());
  }, []);

  if (!allImages || loadingImages) return <AppSpinner />;

  return (
    <Dialog
      modalType="alert"
      open={open}
      onOpenChange={(event, data) => setOpen(data.open)}
    >
      <StyledDialogSurface
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button
                  appearance="subtle"
                  aria-label="close"
                  icon={<Dismiss24Regular />}
                />
              </DialogTrigger>
            }
          >
            {t("hbh.manageImages.label")}
          </DialogTitle>
        </DialogBody>
        <ManageImagesToolbar
          disabled={loadingImages}
          searchValue={searchValue}
          handleSearch={handleSearch}
        />

        {onSelectImage ? null : <AddImages allImages={allImages} />}

        <ListContainer>
          {allImages
            .filter((image) => {
              const imageName = extractFileNameFromUrl(image);
              return imageName.toLowerCase().includes(searchValue);
            })
            .map((img) => {
              return (
                <ImageItem
                  key={img}
                  image={img}
                  setOpen={setOpen}
                  onSelectImage={onSelectImage}
                />
              );
            })}
        </ListContainer>
      </StyledDialogSurface>
    </Dialog>
  );
};

const StyledDialogSurface = styled(DialogSurface)`
  margin-left: 10px;
  margin-right: 10px;
  width: 95%;
  min-height: 80vh;
  min-width: 95%;
  max-height: 98vh;
  padding: 15px;
  margin-top: 10px;

  @media only screen and ${devices.md} {
    width: 900px;
    min-width: 900px;
    padding: 24px;

    left: 10px;
    top: 10px;
  }
`;

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 70vh;
  overflow-y: auto;
`;
